// src/modules/cv/EdgeLab.jsx
// Edge Detection Lab — Sobel, Prewitt, Canny, Laplacian, LoG, Ridge + full compare

import React, { useState, useCallback, useEffect } from 'react';
import { generateSynth, imgToImageData, imageDataToURL, sobel, prewittEdge, laplacianEdge, logEdge, ridgeEdge, cannyEdge } from '../../utils/cvUtils.js';
import { Panel, InfoBox, Metric } from '../../components/ui.jsx';

const TABS = ['upload', 'detect', 'compare', 'canny', 'concepts'];
const Btn = ({onClick,children,color='var(--green)',disabled,style})=>(<button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>);

const ALGORITHMS = [
  {id:'sobel',    label:'Sobel',      color:'var(--green)', desc:'Gx=[-1,0,1;-2,0,2;-1,0,1], Gy=Gxᵀ. mag=√(Gx²+Gy²). Most common general-purpose edge detector.'},
  {id:'prewittX', label:'Prewitt X',  color:'var(--cyan)',  desc:'Simpler than Sobel (uniform weights). Detects vertical edges. [-1,0,1;-1,0,1;-1,0,1].'},
  {id:'prewittY', label:'Prewitt Y',  color:'var(--cyan)',  desc:'Horizontal edge version. [-1,-1,-1;0,0,0;1,1,1]. Use both for full gradient.'},
  {id:'laplacian',label:'Laplacian',  color:'var(--gold)',  desc:'2nd derivative: [0,1,0;1,-4,1;0,1,0]. Isotropic (direction-independent). Sensitive to noise.'},
  {id:'log',      label:'LoG',        color:'var(--orange)','desc':'Laplacian of Gaussian. Blur first (σ≈1.4) then Laplacian. "Mexican hat" wavelet. More robust than raw Laplacian.'},
  {id:'ridge',    label:'Ridge',      color:'var(--violet)', desc:'[-1,-1,-1;-1,8,-1;-1,-1,-1]. Highlights thin structures. Variant of Laplacian with stronger center.'},
  {id:'canny',    label:'Canny',      color:'var(--red)',   desc:'Multi-stage: Gaussian blur → Sobel → Non-Max Suppression → Double threshold + hysteresis. Best general-purpose detector.'},
];

function runAlgo(imgData, algo, cannyLo, cannyHi) {
  switch(algo) {
    case 'sobel':    return sobel(imgData);
    case 'prewittX': return prewittEdge(imgData,'x');
    case 'prewittY': return prewittEdge(imgData,'y');
    case 'laplacian':return laplacianEdge(imgData);
    case 'log':      return logEdge(imgData);
    case 'ridge':    return ridgeEdge(imgData);
    case 'canny':    return cannyEdge(imgData, cannyLo, cannyHi);
    default:         return imgData;
  }
}

const SYNTH = [{name:'Shapes',type:'shapes'},{name:'Checker',type:'checker'},{name:'Circles',type:'circles'},{name:'Gradient',type:'gradient'}];

export default function EdgeLab({ seedImageSrc = null, hideUpload = false }) {
  const [tab, setTab] = useState(hideUpload ? 'detect' : 'upload');
  const [imgSrc, setImgSrc] = useState(null);
  const [imgData, setImgData] = useState(null);
  const [activeAlgo, setActiveAlgo] = useState('sobel');
  const [resultSrc, setResultSrc] = useState(null);
  const [compareResults, setCompareResults] = useState({});
  const [cannyLo, setCannyLo] = useState(50);
  const [cannyHi, setCannyHi] = useState(150);
  const [processing, setProcessing] = useState(false);

  const loadSrc = useCallback(async (src) => {
    setImgSrc(src);
    const id = await imgToImageData(src);
    setImgData(id);
    setResultSrc(null); setCompareResults({});
    if (tab === 'upload') setTab('detect');
  }, [tab]);

  useEffect(() => {
    if (seedImageSrc) loadSrc(seedImageSrc);
  }, [seedImageSrc, loadSrc]);

  useEffect(() => {
    if (hideUpload && tab === 'upload') setTab('detect');
  }, [hideUpload, tab]);

  const handleFile = (file) => {
    if (!file?.type.startsWith('image/')) return;
    const r = new FileReader(); r.onload = e => loadSrc(e.target.result); r.readAsDataURL(file);
  };

  const detect = () => {
    if (!imgData) return;
    const out = runAlgo(imgData, activeAlgo, cannyLo, cannyHi);
    setResultSrc(imageDataToURL(out));
  };

  const runAll = async () => {
    if (!imgData) return;
    setProcessing(true);
    const results = {};
    await new Promise(r => setTimeout(r, 10));
    for (const a of ALGORITHMS) results[a.id] = imageDataToURL(runAlgo(imgData, a.id, cannyLo, cannyHi));
    setCompareResults(results);
    setProcessing(false);
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {(hideUpload ? TABS.filter(t => t !== 'upload') : TABS).map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--green)':'transparent'}`,background:tab===t?'var(--ggreen)':'transparent',color:tab===t?'var(--green)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* UPLOAD */}
      {tab==='upload' && !hideUpload && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}} onDragOver={e=>e.preventDefault()} onClick={()=>document.getElementById('edge-file').click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input id="edge-file" type="file" accept="image/*" style={{display:'none'}} onChange={e=>handleFile(e.target.files[0])}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>⬟</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop Image</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Edges work best on high-contrast images</div>
          </div>
          <Panel title="SYNTHETIC TEST IMAGES" accent="var(--green)">
            {SYNTH.map(({name,type})=>(
              <div key={type} onClick={()=>loadSrc(generateSynth(type,256))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 12px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}} onMouseEnter={e=>e.currentTarget.style.borderColor='var(--green)'} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b0)'}>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div><div style={{fontSize:'10px',color:'var(--t3)'}}>256×256 · ideal for edges</div></div>
                <span style={{color:'var(--green)'}}>→</span>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {/* DETECT */}
      {tab==='detect' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ALGORITHM SELECTION" accent="var(--green)">
            <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'12px'}}>
              {ALGORITHMS.map(a=>(
                <button key={a.id} onClick={()=>setActiveAlgo(a.id)} style={{padding:'7px 14px',border:`1px solid ${activeAlgo===a.id?a.color:'var(--b0)'}`,background:activeAlgo===a.id?a.color+'18':'var(--raised)',color:activeAlgo===a.id?a.color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:activeAlgo===a.id?700:400}}>{a.label}</button>
              ))}
            </div>
            {activeAlgo==='canny' && (
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'12px'}}>
                <div><div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{fontSize:'10px',color:'var(--t3)'}}>Low Threshold</span><span style={{fontSize:'10px',color:'var(--green)',fontWeight:700}}>{cannyLo}</span></div><input type="range" min={10} max={100} step={5} value={cannyLo} onChange={e=>setCannyLo(+e.target.value)} style={{width:'100%',accentColor:'var(--green)'}}/></div>
                <div><div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{fontSize:'10px',color:'var(--t3)'}}>High Threshold</span><span style={{fontSize:'10px',color:'var(--red)',fontWeight:700}}>{cannyHi}</span></div><input type="range" min={50} max={250} step={10} value={cannyHi} onChange={e=>setCannyHi(+e.target.value)} style={{width:'100%',accentColor:'var(--red)'}}/></div>
              </div>
            )}
            <InfoBox color={ALGORITHMS.find(a=>a.id===activeAlgo)?.color||'var(--green)'} icon="⬟">
              {ALGORITHMS.find(a=>a.id===activeAlgo)?.desc}
            </InfoBox>
            <div style={{marginTop:'12px'}}><Btn onClick={detect}>DETECT EDGES →</Btn></div>
          </Panel>
          {(imgSrc || resultSrc) && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <Panel title="ORIGINAL" accent="var(--t2)">
                <img src={imgSrc} alt="original" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/>
              </Panel>
              <Panel title={`EDGES — ${ALGORITHMS.find(a=>a.id===activeAlgo)?.label}`} accent="var(--green)">
                {resultSrc ? <img src={resultSrc} alt="edges" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--green)44'}}/> : <div style={{padding:'40px',textAlign:'center',color:'var(--t3)',fontSize:'11px'}}>Click "DETECT EDGES →" to run</div>}
              </Panel>
            </div>
          )}
        </div>
      )}

      {/* COMPARE ALL */}
      {tab==='compare' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ALL ALGORITHMS SIDE BY SIDE" accent="var(--green)">
            <Btn onClick={runAll} disabled={processing}>{processing?'COMPUTING...':'RUN ALL ALGORITHMS →'}</Btn>
          </Panel>
          {Object.keys(compareResults).length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:'10px'}}>
              <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
                <div style={{fontSize:'10px',color:'var(--t3)',textAlign:'center'}}>ORIGINAL</div>
                <img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/>
              </div>
              {ALGORITHMS.map(a=>compareResults[a.id]&&(
                <div key={a.id} onClick={()=>{setActiveAlgo(a.id);setResultSrc(compareResults[a.id]);setTab('detect');}} style={{display:'flex',flexDirection:'column',gap:'4px',cursor:'pointer'}}>
                  <div style={{fontSize:'10px',color:a.color,textAlign:'center',fontWeight:700}}>{a.label}</div>
                  <img src={compareResults[a.id]} alt={a.id} style={{width:'100%',borderRadius:'3px',border:`1px solid ${a.color}44`,transition:'border-color .15s'}} onMouseEnter={e=>e.currentTarget.style.borderColor=a.color} onMouseLeave={e=>e.currentTarget.style.borderColor=a.color+'44'}/>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CANNY DEEP DIVE */}
      {tab==='canny' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="CANNY EDGE DETECTOR — STEP BY STEP" accent="var(--red)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'14px'}}>
              Canny is considered the optimal edge detector. It minimizes error rate, localization, and produces single-pixel-wide edges. It runs 4 stages:
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr',gap:'8px',marginBottom:'14px'}}>
              {['1. Gaussian Blur — suppress noise before gradient computation','2. Sobel Gradient — compute Gx, Gy, magnitude √(Gx²+Gy²) and angle','3. Non-Max Suppression — thin edges to 1px by zeroing non-local-maxima along gradient direction','4. Double Threshold + Hysteresis — strong edges kept, weak edges kept only if connected to strong'].map((step,i)=>(
                <div key={i} style={{padding:'10px 14px',background:'var(--raised)',borderLeft:`3px solid ${['var(--cyan)','var(--gold)','var(--orange)','var(--red)'][i]}`,borderRadius:'2px',fontSize:'11px',color:'var(--t2)'}}>
                  {step}
                </div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'14px'}}>
              <div><div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{fontSize:'10px',color:'var(--t3)'}}>Low Threshold (weak edges)</span><span style={{fontSize:'10px',color:'var(--green)',fontWeight:700}}>{cannyLo}</span></div><input type="range" min={10} max={100} step={5} value={cannyLo} onChange={e=>setCannyLo(+e.target.value)} style={{width:'100%',accentColor:'var(--green)'}}/></div>
              <div><div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{fontSize:'10px',color:'var(--t3)'}}>High Threshold (strong edges)</span><span style={{fontSize:'10px',color:'var(--red)',fontWeight:700}}>{cannyHi}</span></div><input type="range" min={50} max={250} step={10} value={cannyHi} onChange={e=>setCannyHi(+e.target.value)} style={{width:'100%',accentColor:'var(--red)'}}/></div>
            </div>
            <Btn onClick={()=>{setActiveAlgo('canny');const out=cannyEdge(imgData,cannyLo,cannyHi);setResultSrc(imageDataToURL(out));setTab('detect');}} color="var(--red)">RUN CANNY →</Btn>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="ORIGINAL" accent="var(--t2)">
              <img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/>
            </Panel>
            <Panel title="CANNY RESULT" accent="var(--red)">
              {(() => { const out = cannyEdge(imgData,cannyLo,cannyHi); return <img src={imageDataToURL(out)} alt="canny" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--red)44'}}/>; })()}
            </Panel>
          </div>
        </div>
      )}

      {/* CONCEPTS */}
      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {ALGORITHMS.map(a=>(
              <div key={a.id} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${a.color}33`,borderRadius:'4px'}}>
                <div style={{color:a.color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{a.label}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{a.desc}</div>
              </div>
            ))}
          </div>
          <Panel title="WHEN TO USE WHICH" accent="var(--green)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.8}}>
              <strong style={{color:'var(--green)'}}>Sobel</strong> — general purpose, robust to noise, most used.<br/>
              <strong style={{color:'var(--gold)'}}>Laplacian</strong> — when you need isotropy (direction doesn't matter), but apply Gaussian blur first.<br/>
              <strong style={{color:'var(--orange)'}}>LoG</strong> — pre-smoothed Laplacian, better on noisy images.<br/>
              <strong style={{color:'var(--red)'}}>Canny</strong> — when precision matters. Best overall. Standard in real CV pipelines.<br/>
              <strong style={{color:'var(--cyan)'}}>Prewitt</strong> — simpler weights, faster, slightly worse results than Sobel.<br/>
              <strong style={{color:'var(--violet)'}}>Ridge</strong> — thin linear structures (vessels, cracks, filaments).
            </div>
          </Panel>
        </div>
      )}

      {!imgData && tab!=='upload' && <InfoBox color="var(--green)" icon="⬟">Go to UPLOAD tab to load an image first.</InfoBox>}
    </div>
  );
}
